import React, { useEffect, useRef, useState } from "react";
import { Camera, X } from "lucide-react";

const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

// Hook exposing "pick from gallery" and "take photo" actions plus the hidden inputs/webcam modal they need; picked files go to onImageSelected.
function useImagePicker(onImageSelected) {
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const galleryInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  // Passes the file picked through a hidden input to the caller and resets the input so the same file can be re-picked.
  const handleFileInputChange = (event) => {
    const pickedFile = event.target.files && event.target.files[0];
    event.target.value = "";
    if (pickedFile) onImageSelected(pickedFile);
  };

  // Opens the device gallery / file picker.
  const openGalleryPicker = () => galleryInputRef.current?.click();

  // Opens the native camera on mobile, or the in-page webcam capture on desktop.
  const openCamera = () => {
    const canUseWebcam = !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
    if (isMobileDevice || !canUseWebcam) {
      cameraInputRef.current?.click();
    } else {
      setIsCameraOpen(true);
    }
  };

  // Falls back to the native capture input when the webcam cannot be opened (e.g. permission denied inside the iframe).
  const handleWebcamUnavailable = () => {
    setIsCameraOpen(false);
    cameraInputRef.current?.click();
  };

  const pickerElements = (
    <>
      <input ref={galleryInputRef} type="file" accept="image/*" className="krunk-hidden" onChange={handleFileInputChange} />
      <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" className="krunk-hidden" onChange={handleFileInputChange} />
      {isCameraOpen && (
        <WebcamCaptureModal
          onCapture={(capturedFile) => {
            setIsCameraOpen(false);
            onImageSelected(capturedFile);
          }}
          onClose={() => setIsCameraOpen(false)}
          onUnavailable={handleWebcamUnavailable}
        />
      )}
    </>
  );

  return { openGalleryPicker, openCamera, pickerElements };
}

// Full-screen webcam preview that captures a single JPEG frame and returns it as a File.
function WebcamCaptureModal({ onCapture, onClose, onUnavailable }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [isCameraReady, setIsCameraReady] = useState(false);

  // Starts the webcam stream on mount and stops every track on unmount.
  useEffect(() => {
    let isCancelled = false;
    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: "user" }, audio: false })
      .then((stream) => {
        if (isCancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) videoRef.current.srcObject = stream;
        setIsCameraReady(true);
      })
      .catch((error) => {
        console.error("Unable to open camera:", error);
        if (!isCancelled) onUnavailable();
      });

    return () => {
      isCancelled = true;
      streamRef.current?.getTracks().forEach((track) => track.stop());
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- start the webcam once per modal open

  // Draws the current video frame to a canvas and converts it to a JPEG File.
  const captureCurrentFrame = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob(
      (blob) => {
        if (blob) onCapture(new File([blob], `photo_${Date.now()}.jpg`, { type: "image/jpeg" }));
      },
      "image/jpeg",
      0.9
    );
  };

  return (
    <div className="krunk-fixed krunk-inset-0 krunk-z-[100] krunk-bg-black/80 krunk-flex krunk-flex-col krunk-items-center krunk-justify-center krunk-p-4">
      <button
        type="button"
        onClick={onClose}
        aria-label="Close camera"
        className="krunk-absolute krunk-top-4 krunk-right-4 krunk-text-white krunk-p-2"
      >
        <X className="krunk-w-6 krunk-h-6" />
      </button>

      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        className="krunk-max-w-full krunk-max-h-[70vh] krunk-rounded-lg krunk-bg-black"
      />

      <button
        type="button"
        onClick={captureCurrentFrame}
        disabled={!isCameraReady}
        className="krunk-mt-4 krunk-flex krunk-items-center krunk-gap-2 krunk-bg-white krunk-text-black krunk-font-medium krunk-px-5 krunk-py-2 krunk-rounded-full disabled:krunk-opacity-50"
      >
        <Camera className="krunk-w-4 krunk-h-4" />
        {isCameraReady ? "Capture" : "Starting camera..."}
      </button>
    </div>
  );
}

export default useImagePicker;
